import { useState, useEffect } from "react";
import { authFetch } from "../api";

const SERVICE_LABELS = {
  water: "Water", ors: "ORS", mobile_charging: "Mobile Charging",
  rest_area: "Rest Area", training: "Training",
  first_aid: "First Aid", health_camp: "Health Camp",
};

const STAR = "★";
const EMPTY_STAR = "☆";

function Stars({ rating }) {
  return (
    <span style={{ color: "#f59e0b", fontSize: 15 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n}>{n <= rating ? STAR : EMPTY_STAR}</span>
      ))}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e5e5", borderRadius: 10,
      padding: "18px 22px", minWidth: 140,
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#004d74" }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function AnalyticsDashboard({ apiBase, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [feedback, setFeedback]   = useState(null);
  const [tab, setTab]             = useState("analytics");
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      authFetch(`${apiBase}/analytics/summary`).then(r => r.ok ? r.json() : null),
      authFetch(`${apiBase}/feedback`).then(r => r.ok ? r.json() : []),
    ]).then(([a, f]) => {
      setAnalytics(a);
      setFeedback(f);
      setLoading(false);
    });
  }, [apiBase]);

  const experienceFeedback = feedback?.filter(f => f.type === "experience") ?? [];
  const requestFeedback    = feedback?.filter(f => f.type === "request_center") ?? [];

  const avgRating = experienceFeedback.length > 0
    ? (experienceFeedback.reduce((s, f) => s + (f.rating ?? 0), 0) / experienceFeedback.length).toFixed(1)
    : null;

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "#004d74" }}>Analytics & Feedback</h2>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e5e5", marginBottom: 20 }}>
          {["analytics", "experience", "requests"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={tabStyle(tab === t)}>
              {t === "analytics" ? "Site Analytics" : t === "experience" ? `Experience (${experienceFeedback.length})` : `Center Requests (${requestFeedback.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: "#888", padding: 40 }}>Loading…</div>
        ) : tab === "analytics" ? (
          <AnalyticsTab analytics={analytics} />
        ) : tab === "experience" ? (
          <ExperienceTab items={experienceFeedback} avgRating={avgRating} />
        ) : (
          <RequestsTab items={requestFeedback} />
        )}
      </div>
    </div>
  );
}

function AnalyticsTab({ analytics }) {
  if (!analytics) return <div style={{ color: "#888" }}>No data available.</div>;

  const { total_page_views, by_source, total_directions_clicks, directions_by_center } = analytics;
  const qr     = by_source?.qr     ?? 0;
  const direct = by_source?.direct ?? 0;

  return (
    <div>
      {/* Top stats */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <StatCard label="Site Visits" value={total_page_views} sub="Page views (last 90 days)" />
        <StatCard label="Via QR Code" value={qr} sub={total_page_views > 0 ? `${Math.round(qr / total_page_views * 100)}% of visits` : undefined} />
        <StatCard label="Direct / Link" value={direct} sub={total_page_views > 0 ? `${Math.round(direct / total_page_views * 100)}% of visits` : undefined} />
        <StatCard label="Get Directions" value={total_directions_clicks} sub="Taps across all centers" />
      </div>

      {/* Directions by center */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#333", marginBottom: 10 }}>
          Directions by center
        </div>
        {Object.keys(directions_by_center).length === 0 ? (
          <div style={{ color: "#888", fontSize: 13 }}>No directions clicks yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.entries(directions_by_center).map(([name, count]) => {
              const pct = total_directions_clicks > 0 ? Math.round(count / total_directions_clicks * 100) : 0;
              return (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 200, fontSize: 13, color: "#333", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  <div style={{ flex: 1, background: "#f0f0ee", borderRadius: 4, height: 16, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, background: "#0093dd", height: "100%", borderRadius: 4 }} />
                  </div>
                  <div style={{ width: 60, fontSize: 13, color: "#555", textAlign: "right" }}>{count} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ExperienceTab({ items, avgRating }) {
  return (
    <div>
      {items.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13 }}>No experience feedback yet.</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#004d74" }}>{avgRating}</div>
            <div>
              <Stars rating={Math.round(parseFloat(avgRating))} />
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>avg from {items.length} ratings</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 420, overflowY: "auto" }}>
            {items.map(f => (
              <div key={f.id} style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <Stars rating={f.rating} />
                    {f.center_name && (
                      <span style={{ fontSize: 12, color: "#555", marginLeft: 8 }}>{f.center_name}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                    {f.timestamp ? new Date(f.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
                  </div>
                </div>
                {f.services?.length > 0 && (
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {f.services.map(s => (
                      <span key={s} style={tagStyle}>{SERVICE_LABELS[s] ?? s}</span>
                    ))}
                  </div>
                )}
                {f.comment && (
                  <div style={{ marginTop: 8, fontSize: 13, color: "#333", fontStyle: "italic" }}>"{f.comment}"</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RequestsTab({ items }) {
  return (
    <div>
      {items.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13 }}>No center requests yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 480, overflowY: "auto" }}>
          {items.map(f => (
            <div key={f.id} style={cardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#0093dd", fontWeight: 600 }}>Center Request</div>
                <div style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                  {f.timestamp ? new Date(f.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
                </div>
              </div>
              {f.comment && (
                <div style={{ marginTop: 6, fontSize: 13, color: "#333" }}>{f.comment}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000, padding: 16,
};

const modalStyle = {
  background: "#f7f8fa", borderRadius: 14, padding: 28,
  width: "100%", maxWidth: 700,
  maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
};

const closeBtnStyle = {
  background: "none", border: "none", fontSize: 18,
  cursor: "pointer", color: "#888", lineHeight: 1,
};

function tabStyle(active) {
  return {
    padding: "8px 16px", border: "none", background: "none",
    cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? "#004d74" : "#888",
    borderBottom: active ? "2px solid #004d74" : "2px solid transparent",
    marginBottom: -2,
  };
}

const cardStyle = {
  background: "#fff", border: "1px solid #e5e5e5",
  borderRadius: 8, padding: "12px 14px",
};

const tagStyle = {
  background: "#e8f4fd", color: "#0093dd",
  borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 500,
};
